import { CallbackTranslation } from '../../locale/localizationContext';
import {
  CalculatedOrderStatus,
  Delay,
  MlkAction,
  Problem,
  ShippedProductPaymentMethod,
} from '../../models';
import {
  AssistenceButton,
  ChooseWithdrawPointButton,
  EditAddressButton,
  EditReceptionButton,
  EditReviewButton,
  ReceptionButton,
  ReviewAddressButton,
  ReviewButton,
  ReviewIntercomButton,
  WhenComeBackButton,
} from '../HomeButtons/HomeButtons';

import poImage from '../../assets/images/postal-office.svg';
import protectionModeImage from '../../assets/images/protection-mode.png';
import tooSoonImage from '../../assets/images/too-soon.png';
import standByImage from '../../assets/images/stand-by.png';
import failedImage from '../../assets/images/failed.png';
import missedImage from '../../assets/images/missed.png';
import geocodingKoImage from '../../assets/images/geocoding-ko.png';
import deletedImage from '../../assets/images/deleted.png';
import { ReactElement } from 'react';

type GetImageParams = {
  showPoImage?: boolean;
  orderState: CalculatedOrderStatus;
};

export const getImage = ({ orderState, showPoImage }: GetImageParams) => {
  if (showPoImage) return poImage;

  if (orderState === CalculatedOrderStatus.Missed) return missedImage;
  if (orderState === CalculatedOrderStatus.Failed) return failedImage;
  if (orderState === CalculatedOrderStatus.StandBy) return standByImage;
  if (orderState === CalculatedOrderStatus.TooSoon) return tooSoonImage;
  if (orderState === CalculatedOrderStatus.GeocodingKo) return geocodingKoImage;
  if (orderState === CalculatedOrderStatus.Deleted) return deletedImage;

  if (orderState === CalculatedOrderStatus.ProtectionMode)
    return protectionModeImage;

  return undefined;
};

type GetCardFooterParams = {
  orderState: CalculatedOrderStatus;
  shippedProductPaymentMethod: ShippedProductPaymentMethod;
  cashOnDeliveryAmount: number | undefined;
  pos?: ReactElement;
  t: CallbackTranslation;
};

export const getCardFooter = ({
  cashOnDeliveryAmount,
  orderState,
  shippedProductPaymentMethod,
  pos,
  t,
}: GetCardFooterParams) => {
  const isCorrectOrderStatus = orderState !== CalculatedOrderStatus.Deleted;
  const isCorrectShippedProductPaymentMethod =
    shippedProductPaymentMethod !== ShippedProductPaymentMethod.Bt &&
    shippedProductPaymentMethod !== ShippedProductPaymentMethod.C99 &&
    shippedProductPaymentMethod !== ShippedProductPaymentMethod.Cc &&
    shippedProductPaymentMethod !== ShippedProductPaymentMethod.Db &&
    shippedProductPaymentMethod !== ShippedProductPaymentMethod.Gf &&
    shippedProductPaymentMethod !== ShippedProductPaymentMethod.Pp;
  if (
    !isCorrectShippedProductPaymentMethod ||
    !isCorrectOrderStatus ||
    !cashOnDeliveryAmount
  ) {
    return undefined;
  }
  if (pos && cashOnDeliveryAmount) {
    return pos;
  }

  return <p>{t('home.cashOnDelivery', { amount: cashOnDeliveryAmount })}</p>;
};

type GetAdditionalInfo = {
  delay?: Delay;
  isLastAttempt: boolean;
  noteForCustomer: string | undefined;
  problem: Problem | undefined;
  poOptions?: {
    hour: string | undefined;
    date: string | undefined;
    poName: string | undefined;
  };
  orderState: CalculatedOrderStatus;
  t: CallbackTranslation;
};

export const getAdditionalInfo = ({
  orderState,
  isLastAttempt,
  delay,
  poOptions,
  noteForCustomer,
  problem,
  t,
}: GetAdditionalInfo) => {
  if (poOptions) {
    return t('home.inStockInPo', poOptions);
  }

  if (orderState === CalculatedOrderStatus.GeocodingKo)
    return t('homeAdditionalNotes.geocodingKo');

  if (orderState === CalculatedOrderStatus.Missed)
    return t('homeAdditionalNotes.missed');

  if (orderState === CalculatedOrderStatus.Failed)
    return t('homeAdditionalNotes.failed');

  if (orderState === CalculatedOrderStatus.StandBy)
    return t('homeAdditionalNotes.standBy');

  if (orderState === CalculatedOrderStatus.TooSoon)
    return t('homeAdditionalNotes.tooSoon');

  if (orderState === CalculatedOrderStatus.ProtectionMode)
    return t('homeAdditionalNotes.protectionMode');

  if (
    orderState === CalculatedOrderStatus.InTransit &&
    (delay === Delay.Slight || delay === Delay.Major)
  ) {
    return delay === Delay.Slight
      ? t('home.deliveryInTransitSlightDelay')
      : t('home.deliveryInTransitMajorDelay');
  }

  if (isLastAttempt) return undefined;

  if (
    orderState === CalculatedOrderStatus.Blocked ||
    orderState === CalculatedOrderStatus.ToReschedule
  ) {
    switch (problem) {
      case Problem.F01:
        return t('homeAdditionalNotes.f01');
      case Problem.F02:
        return t('homeAdditionalNotes.f02');
      case Problem.F03:
        return t('homeAdditionalNotes.f03');
      case Problem.F04:
        return t('homeAdditionalNotes.f04');
      case Problem.F05:
        return t('homeAdditionalNotes.f05');
      case Problem.F06:
        return t('homeAdditionalNotes.f06');
      case Problem.F07:
        return t('homeAdditionalNotes.f07');
      case Problem.F08:
        return t('homeAdditionalNotes.f08');
      case Problem.F09:
        return t('homeAdditionalNotes.f09');
      case Problem.F10:
        return t('homeAdditionalNotes.f10');
      case Problem.F11:
        return t('homeAdditionalNotes.f11');
      case Problem.F99:
        return t('homeAdditionalNotes.f99');
    }
  }

  if (orderState === CalculatedOrderStatus.Fulfilled && noteForCustomer)
    return noteForCustomer;

  return undefined;
};

type GetHeadingParameters = {
  isInPo?: boolean;
  problem: Problem | undefined;
  driverName?: string;
  orderState: CalculatedOrderStatus;
  t: CallbackTranslation;
};

export const getHeading = ({
  orderState,
  driverName,
  problem,
  isInPo,
  t,
}: GetHeadingParameters) => {
  if (isInPo) return t('homeHeading.postOffice');

  if (orderState === CalculatedOrderStatus.Deleted)
    return t('homeHeading.deleted');

  if (orderState === CalculatedOrderStatus.GeocodingKo)
    return t('homeHeading.geocodingKo');

  if (orderState === CalculatedOrderStatus.Masked)
    return t('homeHeading.masked');

  if (orderState === CalculatedOrderStatus.ScheduledReception)
    return t('homeHeading.scheduledReception');

  if (orderState === CalculatedOrderStatus.Missed)
    return t('homeHeading.missed');

  if (orderState === CalculatedOrderStatus.Failed)
    return t('homeHeading.failed');

  if (orderState === CalculatedOrderStatus.StandBy)
    return t('homeHeading.standBy');

  if (orderState === CalculatedOrderStatus.TooSoon)
    return t('homeHeading.tooSoon');

  if (orderState === CalculatedOrderStatus.Scheduled)
    return t('homeHeading.scheduled');

  if (orderState === CalculatedOrderStatus.Arriving) {
    if (!driverName) return t('homeHeading.arrivingNoDriver');
    return t('homeHeading.arriving', { driverName });
  }

  if (orderState === CalculatedOrderStatus.InTransit) {
    return t('homeHeading.orderInTransit');
  }

  if (orderState === CalculatedOrderStatus.Fulfilled) {
    return t('homeHeading.orderDelivered');
  }

  if (orderState === CalculatedOrderStatus.ProtectionMode) {
    return t('homeHeading.protectionMode');
  }

  if (orderState === CalculatedOrderStatus.Suspended) {
    return t('homeHeading.suspended');
  }

  switch (problem) {
    case Problem.F03:
    case Problem.F04:
    case Problem.F99:
      return t('homeHeading.failedToDeliverOrder');
    case Problem.F06:
    case Problem.F08:
    case Problem.F11:
      return t('homeHeading.unableToDeliverOrder');
    case Problem.F05:
      return t('homeHeading.rejectedDelivery');
    default:
      return t('homeHeading.notPossibleToDeliverOrder');
  }
};

export const getFrontCardHomeButtons = (
  availableActions: MlkAction[] | null,
  status: CalculatedOrderStatus,
  isPdrVisible: boolean | undefined,
  failureReason?: Problem,
  isExpired: boolean = false,
  isInPo?: boolean
) => {
  if (
    isExpired ||
    isInPo ||
    status === CalculatedOrderStatus.Suspended ||
    status === CalculatedOrderStatus.Arriving ||
    status === CalculatedOrderStatus.Deleted ||
    status === CalculatedOrderStatus.Failed ||
    !availableActions
  ) {
    return [];
  }

  const buttons = [];

  if (availableActions.includes(MlkAction.EditReview)) {
    buttons.push(<EditReviewButton />);
  } else if (availableActions.includes(MlkAction.Review)) {
    buttons.push(<ReviewButton />);
  } else {
    const isAptEnabled = availableActions.includes(MlkAction.Appointment);
    buttons.push(<WhenComeBackButton isDisabled={!isAptEnabled} />);
  }

  // TODO: uncomment when pdr is enabled again
  const isPdrEnabled = availableActions.includes(MlkAction.Pdr);
  if (isPdrVisible) {
    buttons.push(<ChooseWithdrawPointButton isDisabled={!isPdrEnabled} />);
  }

  const isReceptionEnabled = availableActions.includes(MlkAction.Reception);
  if (failureReason === Problem.F02) {
    buttons.push(<EditReceptionButton isDisabled={!isReceptionEnabled} />);
  } else {
    buttons.push(<ReceptionButton isDisabled={!isReceptionEnabled} />);
  }

  const isEditAddress = availableActions.includes(MlkAction.EditAddress);
  const isReviewDoorbell = availableActions.includes(
    MlkAction.EditDoorbellPosition
  );
  const isEditNotes = availableActions.includes(MlkAction.EditNotes);

  if (failureReason === Problem.F03) {
    const isEditNotesEnabled = availableActions.includes(MlkAction.EditNotes);
    buttons.push(<ReviewIntercomButton isDisabled={!isEditNotesEnabled} />);
  } else if (failureReason === Problem.F09 || failureReason === Problem.F04) {
    buttons.push(<ReviewAddressButton isDisabled={!isEditAddress} />);
  } else {
    buttons.push(
      <EditAddressButton
        isDisabled={!isEditAddress && !isReviewDoorbell && !isEditNotes}
      />
    );
  }

  buttons.push(<AssistenceButton />);
  return buttons;
};

export const latRad = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180);
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
};

export const zoom = (mapPx: number, worldPx: number, fraction: number) => {
  return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
};

export const getBounds = (
  firstLat: number,
  secondLat: number,
  firstLng: number,
  secondLng: number
) => {
  if (firstLat > secondLat) {
    if (firstLng > secondLng) {
      const bounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(secondLat, secondLng),
        new window.google.maps.LatLng(firstLat, firstLng)
      );
      return bounds;
    } else {
      const bounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(secondLat, firstLng),
        new window.google.maps.LatLng(firstLat, secondLng)
      );
      return bounds;
    }
  } else {
    if (secondLng > firstLng) {
      const bounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(firstLat, firstLng),
        new window.google.maps.LatLng(secondLat, secondLng)
      );
      return bounds;
    } else {
      const bounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(firstLat, secondLng),
        new window.google.maps.LatLng(secondLat, firstLng)
      );
      return bounds;
    }
  }
};

export const calculateZoomForTwoPoint = ({
  firstLat,
  secondLat,
  firstLng,
  secondLng,
  height,
  width,
}: {
  firstLat: number;
  secondLat: number;
  firstLng: number;
  secondLng: number;
  height?: number;
  width?: number;
}) => {
  let b: any = getBounds(firstLat, secondLat, firstLng, secondLng);

  const WORLD_DIM = { height: 300, width: 448 };
  const ZOOM_MAX = 21;
  const center = b.getCenter();

  const ne = b.getNorthEast();
  const sw = b.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(
    height || WORLD_DIM.height,
    WORLD_DIM.height,
    latFraction
  );
  const lngZoom = zoom(width || WORLD_DIM.width, WORLD_DIM.width, lngFraction);

  return { zoom: Math.min(latZoom, lngZoom, ZOOM_MAX), center: center };
};
